import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "@/lib/api";
import BrandHeader from "@/components/BrandHeader";
import Footer from "@/components/Footer";
import CustomerAccountPrompt from "@/components/CustomerAccountPrompt";
import { Button } from "@/components/ui/button";
import { Copy, CheckCircle2, Loader2 } from "lucide-react";

export default function OrderSuccess() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => navigate("/"));
  }, [id, navigate]);

  if (!order) {
    return (
      <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-yellow-400" />
      </div>
    );
  }

  const copyCode = () => {
    navigator.clipboard.writeText(order.code || "");
    toast.success("Código copiado");
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white relative overflow-hidden">
      <BrandHeader />
      <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" aria-hidden />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-yellow-400/10 blur-3xl pointer-events-none" />

      <main className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-24 text-center">
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", duration: 0.6 }}
          className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-500/20 border-2 border-emerald-500 mb-8"
        >
          <CheckCircle2 className="w-10 h-10 text-emerald-400" />
        </motion.div>

        <div className="text-xs uppercase tracking-[0.35em] text-yellow-400 font-mono-display mb-4">
          Pago confirmado
        </div>
        <h1 className="font-display font-black text-4xl md:text-5xl mb-3">Tu código de despacho</h1>
        <p className="text-zinc-400 mb-10 max-w-lg mx-auto">
          Muestra este código de 6 letras al trabajador de la estación para retirar tus{" "}
          <span className="text-white font-medium">{order.liters ?? order.gallons} litros</span> de{" "}
          <span className="text-yellow-300 font-medium" data-testid="success-fuel">
            {order.fuel_label || "Gasolina B94"}
            {order.fuel_octane ? ` (${order.fuel_octane})` : ""}
          </span>.
        </p>

        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="glass rounded-2xl p-10 md:p-16 mb-8 border-2 border-yellow-400/30"
        >
          <div
            className="font-mono-display font-black text-6xl md:text-8xl lg:text-9xl tracking-[0.2em] text-yellow-400 code-glow"
            data-testid="success-code"
          >
            {order.code}
          </div>
          <Button
            onClick={copyCode}
            data-testid="copy-code-btn"
            className="mt-8 bg-yellow-400 hover:bg-yellow-300 text-black font-bold h-11 px-6"
          >
            <Copy className="w-4 h-4 mr-2" /> Copiar código
          </Button>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-4 text-left">
          <InfoTile
            label="Combustible"
            value={
              order.fuel_label
                ? `${order.fuel_label}${order.fuel_octane ? " · " + order.fuel_octane : ""}`
                : "Gasolina B94"
            }
          />
          <InfoTile label="Litros" value={(order.liters ?? order.gallons ?? 0).toString()} />
          <InfoTile label="Total pagado" value={`$${order.total.toFixed(2)}`} highlight />
        </div>

        <div className="mt-12">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 font-mono-display mb-4 text-center">
            Tu portal personal
          </div>
          <CustomerAccountPrompt email={order.email} name={order.name} />
        </div>

        <div className="mt-10">
          <Button
            variant="outline"
            onClick={() => navigate("/")}
            data-testid="new-order-btn"
            className="border-white/10 bg-transparent text-zinc-300 hover:bg-white/5"
          >
            Realizar otro pedido
          </Button>
        </div>

        <p className="mt-12 text-xs text-zinc-600 font-mono-display tracking-widest">
          ORDEN #{order.id.slice(0, 8).toUpperCase()}
        </p>
      </main>
      <Footer />
    </div>
  );
}

function InfoTile({ label, value, highlight = false }) {
  return (
    <div className="border border-white/10 bg-zinc-900 rounded-xl p-5">
      <div className="text-[11px] uppercase tracking-[0.3em] font-mono-display text-zinc-500 mb-2">
        {label}
      </div>
      <div className={`font-display font-bold text-2xl ${highlight ? "text-yellow-400" : "text-white"}`}>
        {value}
      </div>
    </div>
  );
}
